import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FormBase, LEVEL_BLURB, LEVEL_LABEL, Level, ShareGrant, SharePreview, ShareState,
  avatarIntent, getShareApi, initialsOf, previewShareApi, saveShareApi,
} from '../../api/access';
import { Modal } from '../../components/fs';

/**
 * Who in your account can reach this — one component, two subjects.
 *
 * The header states which subject you are on, because the dialog is opened
 * from three places (a form row's ⋯, the workspace header, a folder's ⋯) and
 * "Share" on its own would leave you guessing which thing you were about to
 * change.
 *
 * Three details carry the whole design, and none of them is decoration:
 *
 *   * INHERITED ROWS ARE A DIFFERENT OBJECT. Well-tinted, grouped under their
 *     own caption, level rendered as static text rather than a select, no ✕,
 *     and the source folder as a link. Nothing about the row invites an edit
 *     that would silently fail — which is the difference between inheritance
 *     being legible and being maddening.
 *   * THE CONSEQUENCE SENTENCE COUNTS BOTH NOUNS. "6 people will no longer see
 *     the 9 forms in this folder", computed on the server before anything is
 *     written. A sentence that estimates teaches people not to read the next one.
 *   * THE FOOTNOTE IS ALWAYS THERE, never a tooltip. Owners and Admins see
 *     every form whatever this dialog says, and a person restricting something
 *     deserves to know that before they save rather than after.
 *
 * Removing the last Manage grant WARNS and ALLOWS (Ben's pick). A refusal would
 * strand the one case where somebody is deliberately handing a form back to
 * the Admins, and the amber sentence names that recovery.
 */
export default function ShareDialog({ kind, id, onClose, onSaved }: {
  kind: 'form' | 'folder';
  id: number;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const [state, setState] = useState<ShareState | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  /** The edit in progress. `base` stays undefined until a radio is moved. */
  const [base, setBase] = useState<FormBase | undefined>(undefined);
  const [baseLevel, setBaseLevel] = useState<'view' | 'edit'>('edit');
  const [grants, setGrants] = useState<ShareGrant[]>([]);
  const [preview, setPreview] = useState<SharePreview | null>(null);

  const [pick, setPick] = useState('');
  const [pickLevel, setPickLevel] = useState<Level>('edit');

  useEffect(() => {
    getShareApi(kind, id)
      .then((r) => {
        setState(r.share);
        setBaseLevel(r.share.baseLevel);
        setGrants(r.share.direct);
      })
      .catch((e) => setError(e.message));
  }, [kind, id]);

  /** What the radios currently say — the stored value, or the effective one. */
  const effectiveBase = base === undefined
    ? (state?.base ?? 'everyone')
    : (base === 'inherit' ? (state?.base ?? 'everyone') : base);

  const body = useMemo(() => ({
    base,
    baseLevel,
    grants: grants.map((g) => ({ subjectType: g.subjectType, subjectId: g.subjectId, level: g.level })),
  }), [base, baseLevel, grants]);

  // The consequence is computed on the server from the change as it stands, so
  // it is true rather than plausible. Re-asked whenever the change moves.
  useEffect(() => {
    if (!state) return;
    let live = true;
    previewShareApi(kind, id, body)
      .then((r) => { if (live) setPreview(r.preview); })
      .catch(() => { if (live) setPreview(null); });
    return () => { live = false; };
  }, [state, kind, id, body]);

  function addPick() {
    if (!pick || !state) return;
    const [type, rawId] = pick.split(':');
    const subjectId = Number(rawId);
    if (grants.some((g) => g.subjectType === type && g.subjectId === subjectId)) { setPick(''); return; }
    const source = type === 'group'
      ? state.candidates.groups.find((g) => g.id === subjectId)
      : state.candidates.users.find((u) => u.id === subjectId);
    if (!source) return;
    setGrants([...grants, {
      subjectType: type as 'user' | 'group',
      subjectId,
      name: source.name,
      email: 'email' in source ? source.email : null,
      active: 'active' in source ? source.active : true,
      memberCount: 'memberCount' in source ? source.memberCount : null,
      level: pickLevel,
      viaFolder: null,
      grantedBy: null,
      grantedAt: new Date().toISOString(),
    }]);
    setPick('');
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await saveShareApi(kind, id, body);
      onSaved?.();
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const title = state
    ? (kind === 'form'
      ? <>Share <b>{state.subject.name}</b></>
      : <>Share the <b>{state.subject.name}</b> folder</>)
    : 'Share…';

  // The primary reads as what it is about to do. "Restrict this folder" is not
  // a nicety: it is the only word on the button that says the change is a
  // narrowing rather than a save.
  const primaryLabel = effectiveBase === 'restricted' && state?.base === 'everyone'
    ? (kind === 'folder' ? 'Restrict this folder' : 'Restrict this form')
    : 'Save access';

  return (
    <Modal
      title={title}
      aside={kind === 'folder' && preview ? `${preview.formCount} forms affected` : undefined}
      onClose={onClose}
      footer={state && (
        <>
          <button className="fs-btn primary" onClick={save} disabled={saving}>{primaryLabel}</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      {error && <div className="ad-err" style={{ marginBottom: 12 }}>{error}</div>}
      {!state && !error && <div>Loading…</div>}

      {state && (
        <>
          <div className="fs-field"><label>Base access</label></div>
          <label className={`fs-choice${effectiveBase === 'everyone' ? ' on' : ''}`}>
            <input
              type="radio"
              checked={effectiveBase === 'everyone'}
              onChange={() => setBase('everyone')}
            />
            <span>
              <b>Everyone in the account</b>
              <small>
                {kind === 'folder'
                  ? `All ${state.candidates.users.length} people can reach the forms in this folder.`
                  : `All ${state.candidates.users.length} people can reach it, at the level you pick.`}
              </small>
            </span>
            {effectiveBase === 'everyone' && (
              <select
                className="fs-input fs-choice-level"
                value={baseLevel}
                onChange={(e) => { setBaseLevel(e.target.value as 'view' | 'edit'); setBase('everyone'); }}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="edit">Edit</option>
                <option value="view">View</option>
              </select>
            )}
          </label>
          <label className={`fs-choice${effectiveBase === 'restricted' ? ' on' : ''}`}>
            <input
              type="radio"
              checked={effectiveBase === 'restricted'}
              onChange={() => setBase('restricted')}
            />
            <span>
              <b>Only the people below</b>
              <small>Plus Owners and Admins, who always see every form.</small>
            </span>
          </label>

          {/* The consequence, counted rather than guessed. Amber when the
              change would leave nobody outside the Admins holding Manage. */}
          {preview && (preview.losingCount > 0 || preview.lastManage) && (
            <div
              className={`fs-consequence${preview.lastManage && !preview.losingCount ? ' warn' : ''}`}
              style={{ marginTop: 12 }}
            >
              {preview.losingCount > 0 && (
                <>
                  <b>
                    {preview.losingCount} {preview.losingCount === 1 ? 'person' : 'people'} will no longer see{' '}
                    {kind === 'folder'
                      ? `the ${preview.formCount} ${preview.formCount === 1 ? 'form' : 'forms'} in this folder`
                      : 'this form'}.
                  </b>{' '}
                  {namesOf(preview.losing)} lose access the moment you save.
                  {!!preview.unaffected.length && (
                    <>
                      {' '}Forms in here that set their own access are unaffected —{' '}
                      <b>{preview.unaffected.length} {preview.unaffected.length === 1 ? 'form' : 'forms'}</b>{' '}
                      keep what they say.
                    </>
                  )}
                </>
              )}
              {preview.lastManage && (
                <div style={{ marginTop: preview.losingCount ? 8 : 0 }}>
                  <b>Nobody outside the Admins will be able to share this.</b>{' '}
                  Changing who can reach it will need an Admin. You can still do this.
                </div>
              )}
            </div>
          )}

          <div className="fs-field" style={{ marginTop: 18 }}><label>Add people or groups</label></div>
          <div className="sh-add">
            <select className="fs-input sh-add-who" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Choose someone…</option>
              {!!state.candidates.groups.length && (
                <optgroup label="Groups">
                  {state.candidates.groups.map((g) => (
                    <option key={`group:${g.id}`} value={`group:${g.id}`}>
                      {g.name} · {g.memberCount} {g.memberCount === 1 ? 'person' : 'people'}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="People">
                {state.candidates.users.map((u) => (
                  <option key={`user:${u.id}`} value={`user:${u.id}`}>
                    {u.name} · {u.email}{u.active ? '' : ' (deactivated)'}
                  </option>
                ))}
              </optgroup>
            </select>
            <select
              className="fs-input sh-add-level"
              value={pickLevel} onChange={(e) => setPickLevel(e.target.value as Level)}
            >
              <option value="edit">Edit</option>
              <option value="view">View</option>
              <option value="manage">Manage</option>
            </select>
            <button className="fs-btn neutral" onClick={addPick} disabled={!pick}>Add</button>
          </div>
          {/* Not an invite flow — §11. Said here rather than left to the build. */}
          <div className="fs-cell-sub" style={{ marginTop: 6 }}>
            Only people already in this account. To add someone new,{' '}
            <Link to="/admin/settings/users">add a user →</Link>
          </div>

          <div className="fs-field" style={{ marginTop: 18 }}>
            <label>Who can reach this {kind}</label>
          </div>
          {!grants.length && !state.inherited.length && (
            <div className="fs-cell-sub">
              Nobody yet, beyond the Owners and Admins.
            </div>
          )}

          {grants.map((g) => (
            <GrantRow
              key={`${g.subjectType}:${g.subjectId}`}
              grant={g}
              onlyManage={grants.filter((x) => x.level === 'manage').length === 1 && g.level === 'manage'}
              onLevel={(level) => setGrants(grants.map((x) => (x === g ? { ...x, level } : x)))}
              onRemove={() => setGrants(grants.filter((x) => x !== g))}
            />
          ))}

          {/* Inherited rows: a different object, deliberately. */}
          {!!state.inherited.length && (
            <div style={{ marginTop: 14 }}>
              <div className="fs-cell-sub" style={{ fontWeight: 600 }}>
                Inherited from the {state.folder?.name} folder
              </div>
              {state.inherited.map((g) => (
                <GrantRow key={`inh:${g.subjectType}:${g.subjectId}`} grant={g} inherited />
              ))}
              <div className="fs-cell-sub" style={{ marginTop: 8 }}>
                Inherited access is changed on the folder.{' '}
                <Link to={`/admin?folder=${state.folder?.id}`} onClick={onClose}>
                  Share the {state.folder?.name} folder →
                </Link>
                {' '}A grant here can widen what the folder gave; it can never take it away.
              </div>
            </div>
          )}

          {/* Always rendered, never a tooltip. */}
          <div className="fs-consequence" style={{ marginTop: 18 }}>
            ⓘ Owners and Admins can see every form in the account, whatever is set here.
          </div>
        </>
      )}
    </Modal>
  );
}

/** "Sam Reed, Ana Koski, Tam Nguyen and 3 others" — the sentence names people. */
function namesOf(people: { name: string }[]): string {
  const names = people.map((p) => p.name);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} others`;
}

function GrantRow({ grant, inherited, onlyManage, onLevel, onRemove }: {
  grant: ShareGrant;
  inherited?: boolean;
  onlyManage?: boolean;
  onLevel?: (level: Level) => void;
  onRemove?: () => void;
}) {
  const intent = avatarIntent(grant.name);
  return (
    <div className={`sh-grant${inherited ? ' inherited' : ''}`}>
      <span
        className={`fs-avatar${grant.active ? '' : ' inactive'}`}
        style={{ background: `var(--${intent}-bg)`, color: `var(--${intent}-fg)` }}
      >
        {initialsOf(grant.name)}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <b style={{ fontSize: 'var(--text-body)' }}>{grant.name}</b>
        <div className="fs-cell-sub">
          {grant.subjectType === 'group'
            ? `${grant.memberCount ?? 0} people`
            : grant.email}
          {!grant.active && ' · deactivated'}
          {inherited && grant.viaFolder && <> · via {grant.viaFolder.name}</>}
          {onlyManage && ' · the only Manage grant'}
        </div>
      </span>
      {inherited ? (
        // Static text, not a select: nothing here invites an edit that would
        // silently fail.
        <span className="fs-pill neutral">{LEVEL_LABEL[grant.level]}</span>
      ) : (
        <>
          <select
            className="fs-input sh-grant-level"
            value={grant.level} title={LEVEL_BLURB[grant.level]}
            onChange={(e) => onLevel?.(e.target.value as Level)}
          >
            <option value="view">View</option>
            <option value="edit">Edit</option>
            <option value="manage">Manage</option>
          </select>
          <button className="fs-btn icon" title="Remove" onClick={onRemove}>✕</button>
        </>
      )}
    </div>
  );
}
